/**
 * CampusHUD
 *
 * Premium dark heads-up display overlay for the campus overview.
 * Shows key company stats and first-time onboarding guidance.
 */

import React, { useState, useEffect } from 'react';
import { Users, Package, TrendingUp, X, ArrowRight } from 'lucide-react';
import { useCompanyMetrics, useCompanyData } from '../../../stores/selectors';
import { useProductStore } from '../../../stores/index';

interface CampusHUDProps {
  onboardingDismissed: boolean;
  onDismissOnboarding: () => void;
}

export const CampusHUD: React.FC<CampusHUDProps> = ({
  onboardingDismissed,
  onDismissOnboarding,
}) => {
  const { reputation } = useCompanyMetrics();
  const { employees } = useCompanyData();
  const products = useProductStore((state) => state.products);

  const employeeCount = employees?.length ?? 0;
  const productCount = products?.length ?? 0;
  const showOnboarding = !onboardingDismissed && employeeCount === 0 && productCount === 0;

  return (
    <>
      {/* Company Stats HUD */}
      <div className="absolute bottom-4 left-4 flex flex-col gap-2 pointer-events-none">
        <div className="bg-gray-900/90 backdrop-blur-md border border-white/10
                        rounded-lg px-3 py-2 shadow-xl pointer-events-auto">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
            <div className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-white/50">Staff</span>
            </div>
            <span className="text-right font-medium text-white">
              {employeeCount}
            </span>

            <div className="flex items-center gap-1.5">
              <Package className="w-3.5 h-3.5 text-cyan-400" />
              <span className="text-white/50">Products</span>
            </div>
            <span className="text-right font-medium text-white">
              {productCount}
            </span>

            <div className="flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-white/50">Rep</span>
            </div>
            <span className="text-right font-medium text-white">
              {reputation?.toFixed(0) ?? 0}
            </span>
          </div>
        </div>
      </div>

      {/* Onboarding hint */}
      {showOnboarding && (
        <OnboardingHint onDismiss={onDismissOnboarding} />
      )}
    </>
  );
};

function OnboardingHint({ onDismiss }: { onDismiss: () => void }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;

  return (
    <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 animate-fadeIn">
      <div className="bg-gray-900/90 backdrop-blur-md border border-white/10
                      rounded-lg px-4 py-3 shadow-xl max-w-sm">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <p className="text-sm font-medium text-white">
              Welcome to your campus
            </p>
            <p className="text-xs text-white/60 mt-1">
              Click the <strong className="text-white">Main Building</strong> to set up your company, then visit the <strong className="text-white">Design Studio</strong> to create your first product.
            </p>
          </div>
          <button
            onClick={onDismiss}
            className="text-white/40 hover:text-white transition-colors p-0.5"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-1 mt-2 text-xs text-emerald-400">
          <ArrowRight className="w-3 h-3" />
          <span>Click a building to enter</span>
        </div>
      </div>
    </div>
  );
}

export default CampusHUD;
